import React, { useLayoutEffect, useRef } from "react";
import { XS_MEASURE_LINE_COLOR } from "../constants";

const XS_SCALE_MIN = 0.6;
const XS_SCALE_MAX = 2;
const XS_SCALE_STEP = 0.2;

/* ============================================================
   ✅ FIX: canvas fills the WHOLE panel body - no empty strip at the bottom.

   The stylesheet gives .stl-xs-canvas / wrapper a fixed (smaller) height,
   possibly with !important, so plain inline styles were not enough.
   We now measure the panel and set the sizes in JS with "important"
   priority, which always wins over the stylesheet. It re-measures on
   every panel resize (+ / − / ⟲ buttons, window resize).
   useCrossSection2D reads the canvas size on draw and redraws on
   resize (ResizeObserver), so the drawing never stretches.
   ============================================================ */
const setImp = (el, prop, value) => {
  if (el) el.style.setProperty(prop, value, "important");
};

const CrossSectionViewer2D = ({
  xsViewerRef,
  xsPos,
  xsScale,
  setXsScale,
  changeXsScale,
  resetXsPos,
  onXsTitlePointerDown,
  crossSectionCanvasRef,
  crossSectionOutline,
}) => {
  const localViewerRef = useRef(null);
  const titleRef = useRef(null);
  const wrapRef = useRef(null);

  // keep the parent's xsViewerRef working (object ref or callback ref)
  const setViewerRef = (el) => {
    localViewerRef.current = el;
    if (typeof xsViewerRef === "function") xsViewerRef(el);
    else if (xsViewerRef) xsViewerRef.current = el;
  };

  useLayoutEffect(() => {
    const viewer = localViewerRef.current;
    const wrap = wrapRef.current;
    if (!viewer || !wrap) return undefined;

    const fit = () => {
      const canvas = crossSectionCanvasRef?.current;
      const title = titleRef.current;

      // panel = column: title on top, canvas area takes the rest
      setImp(viewer, "display", "flex");
      setImp(viewer, "flex-direction", "column");

      const titleH = title ? title.offsetHeight : 0;
      const bodyH = Math.max(80, viewer.clientHeight - titleH);

      setImp(wrap, "flex", "1 1 auto");
      setImp(wrap, "position", "relative");
      setImp(wrap, "width", "100%");
      setImp(wrap, "height", bodyH + "px");
      setImp(wrap, "min-height", "0");
      setImp(wrap, "max-height", "none");
      setImp(wrap, "overflow", "hidden");

      if (canvas) {
        setImp(canvas, "position", "absolute");
        setImp(canvas, "top", "0");
        setImp(canvas, "left", "0");
        setImp(canvas, "width", "100%");
        setImp(canvas, "height", "100%");
        setImp(canvas, "max-height", "none");
        setImp(canvas, "display", "block");
      }
    };

    fit();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => fit());
      ro.observe(viewer);
      if (titleRef.current) ro.observe(titleRef.current);
    }
    window.addEventListener("resize", fit);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [crossSectionCanvasRef, xsScale]);

  return (
    <div
      ref={setViewerRef}
      className={"stl-xs-viewer" + (xsPos ? " free" : "")}
      aria-label="2D cross section view"
      style={{
        "--xs-scale": xsScale,
        ...(xsPos
          ? { left: xsPos.fx * 100 + "%", top: xsPos.fy * 100 + "%" }
          : {}),
      }}
    >
      <div
        ref={titleRef}
        className="stl-xs-viewer-title"
        onPointerDown={onXsTitlePointerDown}
        onContextMenu={(e) => e.preventDefault()}
        onDoubleClick={resetXsPos}
        title="Right-drag to move · double-click to reset position"
        style={{ flex: "0 0 auto" }}
      >
        <span className="stl-xs-viewer-title-text">
          2D Cross section
          <span style={{ color: XS_MEASURE_LINE_COLOR, marginLeft: 6, fontWeight: 700 }}>
            · Click 2 points to measure · Drag points to adjust · Wheel to zoom · Right-drag to pan
          </span>
        </span>
        <div className="stl-xs-size-ctrl">
          <button
            type="button"
            className="stl-xs-size-btn"
            onClick={() => changeXsScale(-XS_SCALE_STEP)}
            disabled={xsScale <= XS_SCALE_MIN}
            title="Make smaller"
            aria-label="Make 2D view smaller"
          >−</button>
          <button
            type="button"
            className="stl-xs-size-btn"
            onClick={() => setXsScale(1)}
            disabled={xsScale === 1}
            title="Default size"
            aria-label="Reset 2D view size"
          >⟲</button>
          <button
            type="button"
            className="stl-xs-size-btn"
            onClick={() => changeXsScale(XS_SCALE_STEP)}
            disabled={xsScale >= XS_SCALE_MAX}
            title="Make bigger"
            aria-label="Make 2D view bigger"
          >+</button>
        </div>
      </div>
      {/* Canvas needs a stable parent wrapper to prevent React removeChild errors */}
      <div
        ref={wrapRef}
        className="stl-xs-canvas-wrapper"
        style={{ flex: "1 1 auto", minHeight: 0, position: "relative" }}
      >
        <canvas ref={crossSectionCanvasRef} className="stl-xs-canvas" />
        {!crossSectionOutline && (
          <div
            className="stl-xs-empty"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            No intersection at current offset
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossSectionViewer2D;